/// Pure helpers for the dashboard's Buyers section — turning one buyer
/// profile into a compact table row (formatting) and slicing the buyer list
/// by the filter chips (All / Flagged / Refunded / Last 30 days / Legacy).
///
/// `formatMinutesShort` originally lived inline in DashboardScreen.tsx's
/// BuyerCard; pulled out here the same way formatCounter.ts was pulled out
/// of TowerLogPage.tsx — dependency-free (no React import) so it's cheaply
/// testable and stays reusable outside the SPA if that's ever needed.
///
/// The canonical BuyerProfile shape lives in DashboardScreen.tsx (mirroring
/// functions/src/buyerProfileCore.ts — see that file's own doc comment on
/// duplicating types across the functions/web boundary by convention). This
/// module does NOT redeclare that whole graph; it only names the NARROW
/// structural slice the helpers below actually read (`BuyerRowSource`).
/// DashboardScreen's richer `BuyerProfile` satisfies it automatically
/// (structural typing), so the richer type flows through filter/sort
/// untouched and the expanded row can still render the full card.

export type RefundOutcome = 'none' | 'requested' | 'refunded'

export interface BuyerRowSource {
  anchor: {
    txn: string
    purchaseDate: string | null
    productIdShort: string | null
    price: number | null
    currency: string | null
  }
  identity: {
    purchasePlace: string | null
  }
  journey: {
    before: {
      minutesToPurchase: number | null
      wallTouches: { count: number } | null
    }
  }
  refund: {
    outcome: RefundOutcome
    riskFlags: string[]
  }
  dataHorizon: {
    trailComplete: boolean
  }
  source: string
}

// ---- Filter chips ---------------------------------------------------------------

export type BuyerFilterKey = 'all' | 'flagged' | 'refunded' | 'recent' | 'legacy'

/// Canonical chip order — All / Flagged / Refunded / Last 30 days / Legacy.
export const BUYER_FILTER_KEYS: readonly BuyerFilterKey[] = ['all', 'flagged', 'refunded', 'recent', 'legacy']

export const BUYER_FILTER_LABELS: Record<BuyerFilterKey, string> = {
  all: 'All',
  flagged: 'Flagged',
  refunded: 'Refunded',
  recent: 'Last 30 days',
  legacy: 'Legacy',
}

/// Window for the "Last 30 days" chip.
export const RECENT_BUYER_DAYS = 30
const DAY_MS = 86_400_000

function matchesFilter(row: BuyerRowSource, filter: BuyerFilterKey, nowMs: number): boolean {
  switch (filter) {
    case 'all':
      return true
    case 'flagged':
      return row.refund.riskFlags.length > 0
    case 'refunded':
      return row.refund.outcome === 'refunded'
    case 'recent': {
      if (!row.anchor.purchaseDate) return false
      const ms = Date.parse(row.anchor.purchaseDate)
      return Number.isFinite(ms) && nowMs - ms <= RECENT_BUYER_DAYS * DAY_MS && ms <= nowMs
    }
    case 'legacy':
      return row.source === 'legacy'
  }
}

/// Rows matching one filter chip. Order is preserved from the input — sort
/// separately (see sortBuyersNewestFirst).
export function filterBuyers<T extends BuyerRowSource>(rows: readonly T[], filter: BuyerFilterKey, nowMs: number): T[] {
  return rows.filter((r) => matchesFilter(r, filter, nowMs))
}

/// The count shown on EVERY chip — always computed over the full unfiltered
/// list, so switching which chip is active never makes another chip's own
/// count appear to move.
export function countBuyerFilters(rows: readonly BuyerRowSource[], nowMs: number): Record<BuyerFilterKey, number> {
  const counts = {} as Record<BuyerFilterKey, number>
  for (const key of BUYER_FILTER_KEYS) counts[key] = filterBuyers(rows, key, nowMs).length
  return counts
}

/// Newest purchase first. A null/unparseable purchaseDate (shouldn't happen
/// post-buildBuyerProfile, but never trust it blindly) sorts last rather
/// than floating to the top or throwing.
export function sortBuyersNewestFirst<T extends BuyerRowSource>(rows: readonly T[]): T[] {
  const ms = (r: T): number => {
    const v = r.anchor.purchaseDate ? Date.parse(r.anchor.purchaseDate) : NaN
    return Number.isFinite(v) ? v : -Infinity
  }
  return [...rows].sort((a, b) => ms(b) - ms(a))
}

// ---- Row-summary formatting ------------------------------------------------------

/// m minutes -> a short "45m" / "5.2h" / "3d" string, mirroring the
/// buildBuyerNarrative timing-phrase buckets in buyerProfileCore.ts (not
/// reusing its exact wording — this is a compact table/stat value, not
/// prose).
export function formatMinutesShort(m: number): string {
  if (m < 60) return `${Math.round(m)}m`
  const h = m / 60
  if (h < 24) return `${h.toFixed(1)}h`
  return `${Math.round(h / 24)}d`
}

/// "Jul 18" — or "Jul 18, 2025" once the purchase is from a different
/// calendar year than `nowMs`, so an eventual multi-year ledger never
/// silently reads as this year. Both sides are read in UTC (the purchase
/// date is ISO 8601 UTC) so this is deterministic regardless of the
/// caller's local time zone — load-bearing for the test suite, which
/// shouldn't depend on the machine running it. null/unparseable -> em-dash.
export function formatDateShort(iso: string | null, nowMs: number): string {
  if (!iso) return '—'
  const ms = Date.parse(iso)
  if (!Number.isFinite(ms)) return '—'
  const d = new Date(ms)
  const base = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
  const thisYear = new Date(nowMs).getUTCFullYear()
  return d.getUTCFullYear() === thisYear ? base : `${base}, ${d.getUTCFullYear()}`
}

/// "USD 4.99" — matches the wording BuyerCard has always used for the full
/// card, so the compact row and the expanded detail never disagree.
export function formatPrice(price: number | null, currency: string | null): string {
  if (price == null) return '—'
  return `${currency ?? 'USD'} ${price.toFixed(2)}`
}

export interface BuyerRowSummary {
  txn: string
  dateShort: string
  product: string
  price: string
  country: string
  timeToBuy: string
  wallTouches: string
  refundOutcome: RefundOutcome
  riskFlagCount: number
  trailComplete: boolean
  isLegacy: boolean
}

/// Flatten one buyer profile into the exact strings the compact table row
/// renders. Badges are derived by the caller straight off refundOutcome /
/// riskFlagCount / trailComplete / isLegacy — kept as plain data here
/// (rather than JSX) so this stays object-in, object-out testable.
export function summarizeBuyerRow(row: BuyerRowSource, nowMs: number): BuyerRowSummary {
  return {
    txn: row.anchor.txn,
    dateShort: formatDateShort(row.anchor.purchaseDate, nowMs),
    product: row.anchor.productIdShort ?? '?',
    price: formatPrice(row.anchor.price, row.anchor.currency),
    country: row.identity.purchasePlace ?? '—',
    timeToBuy: row.journey.before.minutesToPurchase != null ? formatMinutesShort(row.journey.before.minutesToPurchase) : '—',
    wallTouches: row.journey.before.wallTouches ? String(row.journey.before.wallTouches.count) : '—',
    refundOutcome: row.refund.outcome,
    riskFlagCount: row.refund.riskFlags.length,
    trailComplete: row.dataHorizon.trailComplete,
    isLegacy: row.source === 'legacy',
  }
}
